import React, { useEffect, useRef, useState } from 'react';
import UploadDocumentDialog from './UploadDocumentDialog';
import DocumentNotesPanel from './DocumentNotesPanel';
import PartyFields from '../../../PartyFields';
import { PartyFormSection } from '../../../../shared/components/PartyFormFields';
import { entityLabel } from '../../../../shared/components/EntityMultiSelect';
import { relationEntityIds, replaceEntityRelations } from '../../../../shared/utils/entitySelection';
import { getDocumentDueDate } from '../../utils/documents.overview';
import { DOCUMENT_STATUSES } from '../../utils/documents.constants';
import { listClientsResult } from '../../../clients/services/clients.service';
import { listStaffResult } from '../../../staff/services/staff.service';

export default function EditDocumentMetadataDialog({ open, document, clients: suppliedClients, staff: suppliedStaff, categories, onOpenChange, onSubmit }) {
  const [form, setForm] = useState(() => ({ ...document, client: document?.clientLegacyName || document?.client || '', dueDate: document ? getDocumentDueDate(document) : '' }));
  const [catalogs, setCatalogs] = useState({ clients: [], staff: [] });
  const [unavailable, setUnavailable] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  useEffect(() => {
    if (open && document) { setForm({ ...document, client: document.clientLegacyName || document.client || '', dueDate: getDocumentDueDate(document) }); setError(''); }
  }, [open, document]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    Promise.allSettled([suppliedClients ? Promise.resolve({ ok: true, data: suppliedClients }) : listClientsResult(),
      suppliedStaff ? Promise.resolve({ ok: true, data: suppliedStaff }) : listStaffResult()]).then(results => {
      if (!active) return;
      const next = {}, failures = {};
      results.forEach((result, index) => {
        const key = index === 0 ? 'clients' : 'staff';
        failures[key] = result.status !== 'fulfilled' || !result.value?.ok;
        next[key] = failures[key] ? [] : result.value.data || [];
      });
      setCatalogs(next); setUnavailable(failures);
    });
    return () => { active = false; };
  }, [open, suppliedClients, suppliedStaff]);
  function update(field, value) {
    if (pending.current) return;
    setForm(previous => {
      if (field === 'clientId') {
        const client = catalogs.clients.find(item => String(item.id) === String(value));
        return { ...previous, clientId: value, client: client ? entityLabel(client) : value ? previous.client : '' };
      }
      if (field === 'staffIds') return { ...previous,
        relations: replaceEntityRelations(previous.relations, 'TEAM_MEMBER', value, catalogs.staff),
        uploadedBy: value.length ? value.map(id => entityLabel(catalogs.staff.find(item => String(item.id) === String(id)))).join('; ') : document.uploadedBy,
      };
      return { ...previous, [field]: value };
    });
  }
  async function save() {
    if (pending.current) return;
    if (!String(form.name || '').trim()) { setError('Inserisci il titolo del documento.'); return; }
    pending.current = true; setSaving(true); setError('');
    try {
      const result = await onSubmit({ ...form, name: form.name.trim(), uploadedBy: form.uploadedBy || document.uploadedBy || 'Sistema' });
      if (result?.ok === false) setError(result.message || 'Modifica documento non completata.');
      else onOpenChange(false);
    } catch (cause) { setError(cause.message || 'Modifica documento non completata.'); }
    finally { pending.current = false; setSaving(false); }
  }
  if (!document) return null;
  return <UploadDocumentDialog editing open={open} onOpenChange={onOpenChange} onCancel={() => onOpenChange(false)}
    categories={categories}
    isUploading={saving} clients={catalogs.clients} staff={catalogs.staff} clientsUnavailable={unavailable.clients} staffUnavailable={unavailable.staff}
    form={form} error={error} onFieldChange={update} onSubmit={save}
    uploadedFile={{ name: document.originalFilename || document.fileName || document.name, size: document.sizeBytes || document.size || 0 }}
    extraFields={<PartyFormSection title="Stato e altri collegamenti">
      <div className="party-form__field"><label htmlFor="document-status">Stato</label>
        <select id="document-status" value={form.status || 'in_revisione'} disabled={saving} onChange={event => update('status', event.target.value)}>
          {DOCUMENT_STATUSES.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
        </select>
      </div>
      <PartyFields kinds="supplier" value={{ supplierIds: relationEntityIds(form, 'SUPPLIER') }} disabled={saving}
        onSelectionChange={(_, ids, options) => update('relations', replaceEntityRelations(form.relations, 'SUPPLIER', ids, options))} />
    </PartyFormSection>}
    comments={<DocumentNotesPanel documentId={document.id} open={open} />} />;
}
